import React, { useState, useEffect } from 'react';
import { CheckCircle, Clock, FileSearch, Filter, Store, Calendar, ListFilter, Banknote, Trash2, AlertTriangle, Paperclip } from 'lucide-react';
import useOrdenacaoTabela from '../useOrdenacaoTabela';
import { perfilNoModulo } from '../../../utils/perfisDerivados';
// Quem é do escritório e quem é da loja — fonte única das duas telas do Petty Cash.
import { PERFIS_VISAO_TOTAL, PERFIS_DE_LOJA } from './pettycash-perfis';

// Tipo de cada coluna ordenável (ver useOrdenacaoTabela). Fora do componente
// para identidade estável no useMemo.
const TIPOS_ORDENACAO = {
    LOJA: 'texto',
    DATA_COMPRA: 'data',
    NUMERO_CUPOM: 'texto',
    DESCRICAO: 'texto',
    VALOR: 'numero',
    STATUS: 'texto',
};

// Estilo do cabeçalho clicável (cursor de mão; não seleciona texto ao clicar).
const thOrdenavel = { padding: '16px 20px', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

// ---------------------------------------------------------------------------
// Meses no formato 'mm/yyyy' — o mesmo da COMPETENCIA que a API devolve.
// ---------------------------------------------------------------------------
// Estes são o FALLBACK (relógio do navegador). A verdade vem do servidor, em
// GET /api/pettycash/mes-vigente — mas a tela precisa de um valor já no primeiro
// render, antes de a resposta chegar.
const formatarMesAno = (d) => `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;

const mesVigenteNavegador = () => formatarMesAno(new Date());

// ⚠️ PEGADINHA DO DIA 31: `setMonth(getMonth() - 1)` num dia 31 estoura o mês
// curto de destino e volta para o mês seguinte — em 31/07, "menos 1 mês" vira
// 31/06, que não existe, e o JS normaliza para 01/07: o mês anterior sairia como
// JULHO. Zerar o dia para 1 ANTES de subtrair elimina o caso. Acontecia em 5 dias
// do ano (31/03, 31/05, 31/07, 31/10, 31/12) — justamente fim de mês, quando o
// aviso do saque é lido.
const mesAnteriorNavegador = () => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return formatarMesAno(d);
};

// Ordena 'mm/yyyy' pelo CALENDÁRIO, não como texto: comparar a string crua
// ordena pelo MÊS antes do ANO, e em janeiro/2027 o select listaria 12/2026
// acima de 01/2027. A chave 'yyyy-mm' compara na ordem certa.
const chaveOrdemMesAno = (mesAno) => {
    const [mes, ano] = mesAno.split('/');
    return `${ano}-${mes}`;
};

const FinanceiroPettyCashControle = () => {
    // 1. DADOS DO USUÁRIO PARA A TRAVA
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    // Apenas estes perfis podem ver os lançamentos de TODAS as lojas.
    // Qualquer outro perfil (inclusive Gerente / Gerente Plantao) fica travado na própria loja.
    const podeVerTodasLojas = user && PERFIS_VISAO_TOTAL.includes(perfilNoModulo(user.perfil, 'financeiro'));
    const travadoNaLoja = !podeVerTodasLojas;
    // Admin exclui QUALQUER lançamento (ação destrutiva e definitiva, sem trava).
    // ⚠️ `Operacional` (28/09/2026): acesso total ao Financeiro (nível Admin) — mesma alçada.
    const isAdmin = user && (user.perfil === 'Admin' || user.perfil === 'Operacional');
    // Quem está NA LOJA: Gerente / Gerente de Plantão (2026-07-31) e Coordenador
    // de Qualidade (2026-09-17). Os três excluem só o próprio erro recente:
    // lançamento da própria loja, ainda Pendente e com competência no mês vigente.
    // As mesmas travas existem no backend — aqui só evitam oferecer o que não pode.
    // ⚠️ A lista mora em `pettycash-perfis.js` e tem gêmea no backend — ver os
    //    avisos lá antes de mexer.
    const perfilGerenteLoja = user && PERFIS_DE_LOJA.includes(user.perfil);
    const podeExcluirAlgo = isAdmin || perfilGerenteLoja;
    // Esses mesmos perfis só podem confirmar saque de mês já encerrado.
    const perfilEsperaMesVirar = perfilGerenteLoja;

    const [transacoes, setTransacoes] = useState([]);
    const [lojas, setLojas] = useState([]);
    
    // Se o usuário é travado na loja, o filtro já começa com a loja dele
    const [lojaSelecionada, setLojaSelecionada] = useState(travadoNaLoja && user.loja ? user.loja : '');
    // Mês vigente e anterior. Nascem do relógio do NAVEGADOR só para o primeiro
    // render e são substituídos pelos do SERVIDOR assim que a API responde (ver
    // o useEffect logo abaixo) — o backend é quem decide o que é "mês vigente"
    // nas travas de excluir e de sacar, e tablet de loja com data errada existe.
    const [mesVigente, setMesVigente] = useState(mesVigenteNavegador);
    const [mesAnterior, setMesAnterior] = useState(mesAnteriorNavegador);
    // O período abre no MÊS VIGENTE (2026-08-11). Antes abria no mês ANTERIOR
    // (pedido de 2026-07-02), pensando em quem entra para confirmar o saque do mês
    // encerrado. A exclusão pelo gerente (2026-07-31) inverteu a conta: ela só
    // existe no mês vigente, então a tela abria justamente onde nada é excluível
    // — e o gerente concluía que a tela estava quebrada.
    // ⚠️ O critério é FREQUÊNCIA, não importância: saque é 1× por mês; lançar,
    // conferir e corrigir é diário. E o saque tem aviso próprio no card "Valor a
    // Sacar" dizendo qual mês escolher — o lançamento invisível não avisa nada.
    const [mesAnoSelecionado, setMesAnoSelecionado] = useState(mesVigenteNavegador);
    const [statusSelecionado, setStatusSelecionado] = useState('');
    
    const [isCarregando, setIsCarregando] = useState(true);

    // Modal de exclusão (Admin) — guarda o lançamento a excluir e o estado de envio
    const [itemParaExcluir, setItemParaExcluir] = useState(null);
    const [excluindo, setExcluindo] = useState(false);

    // Modal de confirmação do saque — exige anexar o comprovante (2026-07-02).
    // Em celular/tablet o input de imagem já oferece "Tirar foto" nativamente.
    const [modalSaqueAberto, setModalSaqueAberto] = useState(false);
    const [comprovanteSaque, setComprovanteSaque] = useState(null);
    const [salvandoSaque, setSalvandoSaque] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('token') || localStorage.getItem('authToken'); 
        fetch('/api/pettycash/lojas', { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } })
        .then(res => res.json())
        .then(data => { if (Array.isArray(data)) setLojas(data); })
        .catch(err => console.error("Erro ao carregar lojas", err));
    }, []);

    // Alinha o "mês vigente" da tela com o RELÓGIO DO SERVIDOR (2026-08-11).
    // Sem isso, tablet de loja com a data errada abre num mês que o backend não
    // reconhece: o botão de excluir aparece e toma 403, ou some sem motivo.
    // ⚠️ Só move o filtro se o usuário AINDA NÃO o tocou (continua no default).
    // A resposta chega depois do primeiro render — arrastar um mês escolhido à
    // mão seria a tela "pulando" sozinha na frente de quem está usando.
    useEffect(() => {
        const token = localStorage.getItem('token') || localStorage.getItem('authToken');
        const defaultInicial = mesVigenteNavegador();
        fetch('/api/pettycash/mes-vigente', { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } })
        .then(res => res.json())
        .then(data => {
            if (!data || !data.MES_VIGENTE) return;   // falhou: segue o do navegador
            setMesVigente(data.MES_VIGENTE);
            setMesAnterior(data.MES_ANTERIOR);
            setMesAnoSelecionado(atual => (atual === defaultInicial ? data.MES_VIGENTE : atual));
        })
        .catch(err => console.error("Erro ao consultar o mês vigente do servidor", err));
    }, []);

    const carregarTransacoes = () => {
        setIsCarregando(true);
        const token = localStorage.getItem('token') || localStorage.getItem('authToken'); 
        
        // ?incluirAjustes=1 (2026-08-06): traz as linhas de falta/sobra (TIPO='AJUSTE')
        // como registros próprios. Elas contam no saldo, no saque e no lote SAP desde
        // sempre — mas ficavam invisíveis aqui, coladas ao lançamento pai (que pode estar
        // em OUTRO mês). Resultado: o "Total Pendente" da tela não batia com o valor que
        // o saque efetivamente repunha. A aba Auditoria segue sem elas, de propósito.
        fetch('/api/pettycash/listar?incluirAjustes=1', { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } })
        .then(res => { if (!res.ok) throw new Error("Falha ao buscar transações"); return res.json(); })
        .then(data => { if (Array.isArray(data)) setTransacoes(data); })
        .catch(err => console.error(err))
        .finally(() => setIsCarregando(false));
    };

    useEffect(() => { carregarTransacoes(); }, []);

    // Exclui (definitivamente) o lançamento selecionado no modal. Chegam aqui o Admin
    // (qualquer lançamento) e o gerente (só Pendente do mês vigente da própria loja).
    const confirmarExclusao = async () => {
        if (!itemParaExcluir) return;
        setExcluindo(true);
        const token = localStorage.getItem('token') || localStorage.getItem('authToken');
        try {
            const res = await fetch(`/api/pettycash/${itemParaExcluir.ID}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setItemParaExcluir(null);
                carregarTransacoes();
            } else {
                const data = await res.json().catch(() => ({}));
                // O `codigo` (ex.: ER_TABLEACCESS_DENIED_ERROR) só acompanha o 500 do servidor.
                // É ele que separa "não deu certo" de "deu ER_x" para quem opera a tela — sem
                // isso, diagnosticar exige o log do PM2, que a operação não tem como abrir.
                alert((data.error || 'Erro ao excluir o lançamento.') + (data.codigo ? ` [${data.codigo}]` : ''));
            }
        } catch (error) {
            alert('Erro de conexão com o servidor.');
        } finally {
            setExcluindo(false);
        }
    };

    // Abre o modal do saque (as validações de filtro continuam aqui; a
    // confirmação em si agora exige o comprovante anexado no modal).
    const abrirModalSaque = () => {
        if (!lojaSelecionada) {
            alert("Por favor, selecione uma Loja no filtro para realizar a reposição do caixa.");
            return;
        }

        if (!mesAnoSelecionado) {
            alert("Selecione o mês que deseja confirmar o saque.");
            return;
        }

        if (saqueBloqueadoPorMes) {
            alert("O saque só pode ser confirmado após o encerramento do mês. Aguarde o mês virar.");
            return;
        }

        setComprovanteSaque(null);
        setModalSaqueAberto(true);
    };

    const confirmarSaqueLoja = async () => {
        if (!comprovanteSaque) {
            alert("Anexe a imagem do comprovante do saque para confirmar.");
            return;
        }

        const token = localStorage.getItem('token') || localStorage.getItem('authToken');
        setSalvandoSaque(true);

        try {
            // Multipart: periodo + arquivo do comprovante (o navegador define o
            // Content-Type com o boundary — não setar na mão)
            const formData = new FormData();
            formData.append('periodo', mesAnoSelecionado);
            formData.append('comprovante', comprovanteSaque);

            const response = await fetch(`/api/pettycash/sacar/${encodeURIComponent(lojaSelecionada)}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (response.ok) {
                setModalSaqueAberto(false);
                alert("Caixa reposto e limite restaurado com sucesso!");
                carregarTransacoes();
            } else {
                const data = await response.json();
                alert(data.error || "Erro ao processar reposição.");
            }
        } catch (error) {
            alert("Falha na conexão com o servidor.");
        } finally {
            setSalvandoSaque(false);
        }
    };

    // ========================================================================
    // LÓGICA DOS FILTROS E CÁLCULOS
    // ========================================================================
    // O mês de cada lançamento é a COMPETÊNCIA (mês que conta no caixa, decidido
    // pelo Financeiro num "Não Validado") — não mais o mês da data da compra.
    // Isso mantém o filtro alinhado com o /sacar, que também agrupa por competência.
    // Fallback pro mês da compra cobre a janela entre migration e deploy.
    const mesDoLancamento = (t) => t.COMPETENCIA || t.DATA_COMPRA.substring(3);

    // Linha de falta/sobra (o Financeiro lança no "Não Validado" da aba Auditoria).
    // Não é despesa: não tem cupom, arquivo nem quantidade, e o valor vem ASSINADO
    // (+ acrescentar / − tirar). Aparece aqui no mês da COMPETÊNCIA dela — que
    // costuma ser diferente do mês do lançamento que a originou.
    const ehAjuste = (t) => t.TIPO === 'AJUSTE';

    // Quem pode excluir CADA lançamento. Para o gerente as travas são as do backend:
    // ainda Pendente (o caixa não foi reposto) e competência no mês vigente — depois
    // disso o lançamento já virou dinheiro/contabilidade e a correção é do Financeiro.
    // ⚠️ Ajuste NUNCA é excluível por aqui — nem pelo Admin. Ele é filho de uma decisão
    // do Financeiro na Auditoria e some por lá (desmarcando a falta/sobra); e o
    // DELETE /:id já derruba o ajuste junto quando o lançamento PAI é excluído.
    const podeExcluirLancamento = (t) =>
        !ehAjuste(t) && (isAdmin || (perfilGerenteLoja && t.STATUS === 'Pendente' && mesDoLancamento(t) === mesVigente));

    // Vigente e anterior entram SEMPRE na lista, tenham lançamento ou não:
    //   • o vigente é o default do filtro — sem ele, uma loja que ainda não lançou
    //     nada no mês abriria com o select vazio e o filtro ativo (tela "quebrada");
    //   • o anterior é onde mora o saque, e o card manda ir até ele — de nada
    //     adianta o aviso se o mês não estiver no select para ser escolhido.
    const periodosDisponiveis = Array.from(new Set([...transacoes.map(mesDoLancamento), mesVigente, mesAnterior]))
        .sort((a, b) => chaveOrdemMesAno(b).localeCompare(chaveOrdemMesAno(a)));   // mais recente primeiro

    const transacoesBaseCards = transacoes.filter(t => {
        const passaFiltroLoja = lojaSelecionada ? t.LOJA === lojaSelecionada : true;
        const passaFiltroPeriodo = mesAnoSelecionado ? mesDoLancamento(t) === mesAnoSelecionado : true;
        return passaFiltroLoja && passaFiltroPeriodo;
    });

    const transacoesFiltradas = transacoesBaseCards.filter(t => {
        return statusSelecionado ? t.STATUS === statusSelecionado : true;
    });

    // Ordenação clicável pelo cabeçalho da tabela (ver useOrdenacaoTabela).
    const { dadosOrdenados, ordenar, indicador } = useOrdenacaoTabela(transacoesFiltradas, TIPOS_ORDENACAO);

    // Limite total = soma dos limites de cada loja (vem do backend por loja, pois
    // há lojas com teto diferente do padrão R$ 600, ex.: PCI = R$ 700). Com uma
    // loja selecionada, é o limite dela; na visão geral, a soma de toda a rede.
    const lojasConsideradas = lojaSelecionada
        ? lojas.filter(l => l.NOME_LOJA === lojaSelecionada)
        : lojas;
    const limiteTotalCalculado = lojasConsideradas.reduce(
        (acc, l) => acc + (Number(l.LIMITE_MENSAL) || 600.00),
        0
    );

    // VALOR_COMPUTADO = o que efetivamente conta no caixa (fallback pro VALOR
    // original enquanto o backend não devolver o campo novo).
    const totalPendente = transacoesBaseCards
        .filter(t => t.STATUS === 'Pendente')
        .reduce((acc, curr) => acc + parseFloat(curr.VALOR_COMPUTADO ?? curr.VALOR), 0);

    const limiteDisponivel = Math.max(0, limiteTotalCalculado - totalPendente);

    // O mês do filtro vem como 'mm/yyyy'. Encerrado = anterior ao mês vigente.
    // Compara contra o `mesVigente` (do servidor) e não contra um `new Date()`
    // próprio: a trava real do saque está no PUT /sacar/:loja, e a tela mostrando
    // um mês diferente do backend só produz botão que promete e depois nega.
    const mesSelecionadoFechado = !!mesAnoSelecionado
        && chaveOrdemMesAno(mesAnoSelecionado) < chaveOrdemMesAno(mesVigente);

    // Trava de saque para Gerente / Gerente de Plantão: só liberam mês já encerrado.
    const saqueBloqueadoPorMes = perfilEsperaMesVirar && !mesSelecionadoFechado;

    const inputStyle = { padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', width: '220px' };

    return (
        <div>
            {/* BARRA DE FILTROS */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '24px', marginBottom: '24px', backgroundColor: '#fff', padding: '16px 24px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b' }}>
                    <Filter size={18} /> <span style={{ fontWeight: '600', fontSize: '14px' }}>Filtros de Visão:</span>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }}>
                    <Store size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px' }} />
                    <select 
                        value={lojaSelecionada} 
                        onChange={(e) => setLojaSelecionada(e.target.value)} 
                        style={{ ...inputStyle, paddingLeft: '36px', backgroundColor: travadoNaLoja ? '#f1f5f9' : '#fff' }}
                        disabled={travadoNaLoja} // BLOQUEIA O FILTRO SE NÃO PUDER VER TODAS AS LOJAS
                    >
                        <option value="">Todas as Lojas</option>
                        {travadoNaLoja && !lojas.find(l => l.NOME_LOJA === user.loja) && (
                            <option value={user.loja}>{user.loja}</option>
                        )}
                        {lojas?.map((l, i) => <option key={i} value={l.NOME_LOJA}>{l.NOME_LOJA}</option>)}
                    </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }}>
                    <Calendar size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px' }} />
                    <select value={mesAnoSelecionado} onChange={(e) => setMesAnoSelecionado(e.target.value)} style={{...inputStyle, paddingLeft: '36px'}}>
                        <option value="">Todo o Período</option>
                        {periodosDisponiveis.map((periodo, i) => <option key={i} value={periodo}>{periodo}</option>)}
                    </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }}>
                    <ListFilter size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px' }} />
                    <select value={statusSelecionado} onChange={(e) => setStatusSelecionado(e.target.value)} style={{...inputStyle, paddingLeft: '36px'}}>
                        <option value="">Todos os Status</option>
                        <option value="Pendente">Apenas Pendentes (A Repor)</option>
                        <option value="Aprovado">Apenas Aprovados (Já Reposto)</option>
                    </select>
                </div>
            </div>

            {/* CARDS DE RESUMO GERENCIAL */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '24px' }}>
                <div style={{ padding: '24px', backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Limite Disponível</p>
                    <h3 style={{ margin: 0, fontSize: '28px', color: limiteDisponivel === 0 ? '#ef4444' : '#0f172a' }}>
                        R$ {limiteDisponivel.toFixed(2)}
                    </h3>
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#94a3b8' }}>
                        {lojaSelecionada ? `Caixa atual da loja` : `Somatório da rede`}
                    </p>
                </div>
                
                <div style={{ padding: '24px', backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                    <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: '700', color: '#64748b' }}>TOTAL DESPESAS</p>
                    <h3 style={{ margin: 0, fontSize: '28px', color: '#f59e0b' }}>R$ {totalPendente.toFixed(2)}</h3>
                    {/* "e ajustes" desde 2026-08-06: a falta/sobra passou a entrar nesta soma —
                        é o que o saque efetivamente repõe (o /sacar nunca filtrou por TIPO). */}
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#94a3b8' }}>Recibos e ajustes pendentes de reposição</p>
                </div>
                
                <div style={{ padding: '24px', backgroundColor: '#16a39c', borderRadius: '12px', color: '#fff', boxShadow: '0 4px 6px rgba(22, 163, 156, 0.2)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <div>
                        <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: '700', color: '#ccfbf1' }}>VALOR A SACAR</p>
                        <h3 style={{ margin: 0, fontSize: '28px' }}>R$ {totalPendente.toFixed(2)}</h3>
                    </div>
                    
                    {(() => {
                        const msgStyle = { margin: '16px 0 0 0', fontSize: '12px', color: '#ccfbf1', opacity: 0.8 };
                        if (!lojaSelecionada) return <p style={msgStyle}>Selecione uma loja p/ repor</p>;
                        if (totalPendente <= 0) return <p style={msgStyle}>Nenhum valor pendente</p>;
                        if (!mesAnoSelecionado) return <p style={msgStyle}>Selecione o mês para confirmar o saque</p>;
                        // Aviso ACIONÁVEL (2026-08-11): com a tela abrindo no mês vigente,
                        // é aqui que o gerente cai quando vem confirmar o saque. Dizer só
                        // "não dá" o deixa parado — a mensagem nomeia o mês a escolher.
                        if (saqueBloqueadoPorMes) return <p style={msgStyle}>Selecione {mesAnterior} — o saque só abre com o mês encerrado</p>;
                        return (
                            <button onClick={abrirModalSaque} style={{ marginTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px', backgroundColor: '#fff', color: '#16a39c', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                <Banknote size={16} /> Confirmar Saque
                            </button>
                        );
                    })()}
                </div>
            </div>

            {/* TABELA GERAL */}
            <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                <div style={{ padding: '20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', color: '#0f172a' }}>
                        Histórico de Recibos 
                        {lojaSelecionada && ` - ${lojaSelecionada}`}
                    </h3>
                    {isCarregando && <span style={{ fontSize: '12px', color: '#64748b' }}>Atualizando dados...</span>}
                </div>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '900px' }}>
                        <thead style={{ backgroundColor: '#f8fafc', fontSize: '13px', color: '#64748b', textTransform: 'uppercase' }}>
                            <tr>
                                <th onClick={() => ordenar('LOJA')} style={thOrdenavel}>Loja{indicador('LOJA')}</th>
                                <th onClick={() => ordenar('DATA_COMPRA')} style={thOrdenavel}>Data{indicador('DATA_COMPRA')}</th>
                                <th onClick={() => ordenar('NUMERO_CUPOM')} style={thOrdenavel}>Nº Cupom{indicador('NUMERO_CUPOM')}</th>
                                <th onClick={() => ordenar('DESCRICAO')} style={thOrdenavel}>Descrição / Categoria{indicador('DESCRICAO')}</th>
                                <th onClick={() => ordenar('VALOR')} style={thOrdenavel}>Valor{indicador('VALOR')}</th>
                                <th onClick={() => ordenar('STATUS')} style={thOrdenavel}>Status{indicador('STATUS')}</th>
                                <th style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}>Arquivo</th>
                                {podeExcluirAlgo && <th style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', textAlign: 'center' }}>Ações</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {transacoesFiltradas.length === 0 && !isCarregando ? (
                                <tr>
                                    <td colSpan={podeExcluirAlgo ? 8 : 7} style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>
                                        Nenhuma transação encontrada para este filtro.
                                    </td>
                                </tr>
                            ) : (
                                dadosOrdenados.map((t) => (
                                    <tr key={t.ID} style={{ borderBottom: '1px solid #e2e8f0', transition: 'background 0.2s', backgroundColor: ehAjuste(t) ? '#fffbeb' : (t.STATUS === 'Aprovado' ? '#f8fafc' : '#fff') }}>
                                        <td style={{ padding: '16px 20px', fontSize: '13px', color: '#0f172a', fontWeight: '600' }}>{t.LOJA}</td>
                                        <td style={{ padding: '16px 20px', fontSize: '13px', color: '#475569' }}>
                                            {t.DATA_COMPRA}
                                            {/* Competência remanejada pelo Financeiro: conta em outro mês.
                                                No ajuste as duas são iguais por construção (a data é só âncora),
                                                então o aviso não dispara — e não deve mesmo. */}
                                            {t.COMPETENCIA && t.COMPETENCIA !== t.DATA_COMPRA.substring(3) && (
                                                <div style={{ fontSize: '11px', color: '#b91c1c', fontWeight: '600' }}>conta em {t.COMPETENCIA}</div>
                                            )}
                                        </td>
                                        <td style={{ padding: '16px 20px', fontSize: '13px', color: '#475569', fontWeight: '500' }}>
                                            {/* Ajuste não tem cupom: o traço aqui é a ausência esperada, não falta de dado */}
                                            {ehAjuste(t) ? <span style={{ color: '#cbd5e1' }}>—</span> : (t.NUMERO_CUPOM || '-')}
                                        </td>
                                        <td style={{ padding: '16px 20px', fontSize: '13px', color: '#475569' }}>
                                            {ehAjuste(t) && (
                                                <span style={{ display: 'inline-block', marginBottom: '4px', padding: '2px 8px', borderRadius: '999px', backgroundColor: '#fef3c7', color: '#b45309', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                                    Falta / Sobra
                                                </span>
                                            )}
                                            {ehAjuste(t) && <br/>}
                                            {t.DESCRICAO} <br/>
                                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>Conta: {t.CONTA_DESPESA}</span>
                                        </td>
                                        <td style={{ padding: '16px 20px', fontSize: '14px', fontWeight: '700', color: ehAjuste(t) && parseFloat(t.VALOR_COMPUTADO) < 0 ? '#b45309' : '#0f172a' }}>
                                            {/* No ajuste o sinal é a informação principal (− tirou do caixa,
                                                + acrescentou), então ele vem na frente do R$ em vez de virar
                                                um "R$ -36.90" que se lê como erro de formatação. */}
                                            {ehAjuste(t)
                                                ? `${parseFloat(t.VALOR_COMPUTADO) < 0 ? '−' : '+'}R$ ${Math.abs(parseFloat(t.VALOR_COMPUTADO)).toFixed(2)}`
                                                : `R$ ${parseFloat(t.VALOR_COMPUTADO ?? t.VALOR).toFixed(2)}`}
                                            {/* Valor corrigido pelo Financeiro num "Não Validado": mostra o original riscado */}
                                            {!ehAjuste(t) && t.VALOR_COMPUTADO != null && parseFloat(t.VALOR_COMPUTADO) !== parseFloat(t.VALOR) && (
                                                <div style={{ fontSize: '11px', color: '#94a3b8', textDecoration: 'line-through', fontWeight: '400' }}>R$ {parseFloat(t.VALOR).toFixed(2)}</div>
                                            )}
                                        </td>
                                        <td style={{ padding: '16px 20px' }}>
                                            {t.STATUS === 'Pendente' ? (
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '999px', backgroundColor: '#fef3c7', color: '#d97706', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase' }}>
                                                    <Clock size={12} /> Pendente
                                                </span>
                                            ) : (
                                                <span style={{ display: 'inline-flex', flexDirection: 'column', gap: '3px' }}>
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '999px', backgroundColor: '#e2e8f0', color: '#64748b', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase' }}>
                                                        <CheckCircle size={12} /> Reposto
                                                    </span>
                                                    {/* Comprovante do saque que repôs este lançamento (exigido desde 2026-07-02) */}
                                                    {t.SAQUE_COMPROVANTE_URL && (
                                                        <a href={t.SAQUE_COMPROVANTE_URL} target="_blank" rel="noopener noreferrer"
                                                            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#16a39c', fontWeight: '600', textDecoration: 'none' }}>
                                                            <Paperclip size={11} /> Comprovante
                                                        </a>
                                                    )}
                                                </span>
                                            )}
                                        </td>
                                        <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                                            {t.CUPOM_URL ? (
                                                <a href={t.CUPOM_URL} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', backgroundColor: '#fff', cursor: 'pointer', color: '#16a39c' }} title="Ver Arquivo no SharePoint">
                                                    <FileSearch size={16} />
                                                </a>
                                            ) : '-'}
                                        </td>
                                        {podeExcluirAlgo && (
                                            <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                                                {podeExcluirLancamento(t) ? (
                                                    <button
                                                        onClick={() => setItemParaExcluir(t)}
                                                        title="Excluir lançamento"
                                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '6px 12px', backgroundColor: '#fff', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                                                    >
                                                        <Trash2 size={14} /> Excluir
                                                    </button>
                                                ) : (
                                                    // Fora da janela do gerente: mostra o motivo no tooltip em vez de
                                                    // simplesmente sumir com o botão (que pareceria falha da tela).
                                                    <span
                                                        title={ehAjuste(t)
                                                            ? 'Falta/sobra lançada pelo Financeiro na aba Auditoria — é lá que ela se altera ou sai.'
                                                            : (t.STATUS !== 'Pendente'
                                                                ? 'Caixa já reposto — a exclusão passa a ser do Financeiro.'
                                                                : `Só é possível excluir lançamentos do mês vigente (${mesVigente}).`)}
                                                        style={{ color: '#cbd5e1', fontSize: '13px' }}
                                                    >
                                                        —
                                                    </span>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* MODAL: Confirmar saque com comprovante (obrigatório desde 2026-07-02) */}
            {modalSaqueAberto && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                    <div style={{ backgroundColor: '#fff', padding: '30px', borderRadius: '14px', width: '90%', maxWidth: '460px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                            <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#e0f2f1', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Banknote size={18} color="#16a39c" />
                            </div>
                            <h3 style={{ margin: 0, color: '#0f766e', fontSize: '18px', fontWeight: '700' }}>Confirmar saque e reposição</h3>
                        </div>

                        <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px', fontSize: '14px', color: '#334155' }}>
                            <div><strong>Loja:</strong> {lojaSelecionada}</div>
                            <div><strong>Mês:</strong> {mesAnoSelecionado}</div>
                            <div><strong>Valor a sacar:</strong> R$ {totalPendente.toFixed(2)}</div>
                        </div>

                        <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 10px 0', lineHeight: '1.5' }}>
                            Isso zera as pendências do mês e restaura o limite da unidade.
                            <strong> Anexe a imagem do comprovante do saque</strong> — no celular ou tablet dá para tirar a foto na hora.
                        </p>

                        {/* accept="image/*": em smartphone/tablet o navegador já oferece "Tirar foto" */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '14px', border: `2px dashed ${comprovanteSaque ? '#16a39c' : '#cbd5e1'}`, borderRadius: '10px', cursor: 'pointer', marginBottom: '16px', backgroundColor: comprovanteSaque ? '#f0fdfa' : '#fff' }}>
                            <Paperclip size={18} color={comprovanteSaque ? '#16a39c' : '#94a3b8'} />
                            <span style={{ fontSize: '13px', color: comprovanteSaque ? '#0f766e' : '#64748b', fontWeight: comprovanteSaque ? '600' : '400', wordBreak: 'break-all' }}>
                                {comprovanteSaque ? comprovanteSaque.name : 'Anexar comprovante (foto ou arquivo de imagem)'}
                            </span>
                            <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={e => setComprovanteSaque(e.target.files?.[0] || null)}
                            />
                        </label>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button
                                onClick={() => setModalSaqueAberto(false)}
                                disabled={salvandoSaque}
                                style={{ padding: '10px 18px', backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmarSaqueLoja}
                                disabled={salvandoSaque || !comprovanteSaque}
                                title={!comprovanteSaque ? 'Anexe o comprovante para liberar a confirmação' : undefined}
                                style={{ padding: '10px 18px', backgroundColor: (salvandoSaque || !comprovanteSaque) ? '#99d5d1' : '#16a39c', color: '#fff', border: 'none', borderRadius: '8px', cursor: (salvandoSaque || !comprovanteSaque) ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                                <Banknote size={15} /> {salvandoSaque ? 'Enviando...' : 'Confirmar Saque'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL: Confirmar exclusão (Admin sem trava | gerente no mês vigente) */}
            {itemParaExcluir && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                    <div style={{ backgroundColor: '#fff', padding: '30px', borderRadius: '14px', width: '90%', maxWidth: '460px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                            <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Trash2 size={18} color="#dc2626" />
                            </div>
                            <h3 style={{ margin: 0, color: '#b91c1c', fontSize: '18px', fontWeight: '700' }}>Excluir lançamento</h3>
                        </div>
                        <p style={{ color: '#64748b', fontSize: '14px', margin: '0 0 16px 0', lineHeight: '1.5' }}>
                            Esta ação é <strong>definitiva</strong> e não pode ser desfeita. Confirme os dados do lançamento:
                        </p>
                        <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px', fontSize: '14px', color: '#334155' }}>
                            <div><strong>Loja:</strong> {itemParaExcluir.LOJA}</div>
                            <div><strong>Data:</strong> {itemParaExcluir.DATA_COMPRA}</div>
                            <div><strong>Descrição:</strong> {itemParaExcluir.DESCRICAO}</div>
                            <div><strong>Nº Cupom:</strong> {itemParaExcluir.NUMERO_CUPOM || '-'}</div>
                            <div><strong>Valor:</strong> R$ {parseFloat(itemParaExcluir.VALOR || 0).toFixed(2)}</div>
                        </div>
                        {(itemParaExcluir.STATUS === 'Aprovado' || itemParaExcluir.SAP_LOTE_ID) && (
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '10px 14px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', marginBottom: '16px' }}>
                                <AlertTriangle size={16} color="#d97706" style={{ flexShrink: 0, marginTop: '1px' }} />
                                <span style={{ fontSize: '13px', color: '#92400e' }}>
                                    Atenção: este lançamento já foi {itemParaExcluir.SAP_LOTE_ID ? 'exportado para o SAP' : 'reposto'}. Excluí-lo apaga o registro
                                    {itemParaExcluir.SAP_LOTE_ID ? ' e o desvincula do lote SAP já gerado' : ''}.
                                </span>
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button
                                onClick={() => setItemParaExcluir(null)}
                                disabled={excluindo}
                                style={{ padding: '10px 18px', backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmarExclusao}
                                disabled={excluindo}
                                style={{ padding: '10px 18px', backgroundColor: excluindo ? '#fca5a5' : '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', cursor: excluindo ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                                <Trash2 size={15} /> {excluindo ? 'Excluindo...' : 'Excluir definitivamente'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FinanceiroPettyCashControle;